import { useCallback, useRef } from "react";
import { useNavigation } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import type { NavigationState } from "@react-navigation/routers";
import { useSetAtom } from "jotai";
import { RootStackParamList } from "../navigations/RootNavigation";
import { EarningType } from "../__generated__/graphql";
import { editThirteenthSalaryCallback, ThirteenthSalaryEditorResult } from "../atoms/core";
import { extractLastRouteName } from "./use-vacation-editor";

type ScreenParams = StackScreenProps<RootStackParamList>;

type CallBackType = (result: ThirteenthSalaryEditorResult) => void | Promise<void>;

/**
 * Brings the 13th salary editor on screen (13th on/off, 12x/13x for monthly contracts).
 * usage:
 * const editThirteenthSalary = useThirteenthSalaryEditor();
 * editThirteenthSalary({ has13thSalary, pay13thSalaryWithSalary, isMandatory, earningType }, (result) => {
 *       // result.has13thSalary / result.pay13thSalaryWithSalary have been chosen
 *   });
 */
export const useThirteenthSalaryEditor = () => {
    const callbackRef = useRef<{
        callback: CallBackType;
        state: NavigationState<RootStackParamList>;
    }>(undefined);
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const setOnSave = useSetAtom(editThirteenthSalaryCallback);
    return useCallback(
        (
            options: {
                has13thSalary: boolean;
                pay13thSalaryWithSalary: boolean;
                isMandatory: boolean;
                earningType: EarningType;
            },
            onChange: CallBackType,
        ) => {
            callbackRef.current = { callback: onChange, state: navigation.getState() };

            const onSave = async (result: ThirteenthSalaryEditorResult) => {
                const callback = callbackRef.current?.callback;
                const savedState = callbackRef.current?.state;

                await callback?.(result);

                callbackRef.current = undefined;

                if (savedState) {
                    const lastRouteName = extractLastRouteName(savedState);
                    if (lastRouteName) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        navigation.navigate(lastRouteName);
                        return;
                    }
                }
                navigation.pop();
            };
            setOnSave({ func: onSave });
            navigation.navigate("ThirteenthSalaryEditorNavScreen", options);
        },
        [navigation, setOnSave],
    );
};
